import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Alert,
  Fab,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  SmartToy as AgentIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useGetAgentsQuery } from '../../../store/slices/apiSlice';
import AgentList from '../components/AgentList';
import AgentForm from '../components/AgentForm';
import AgentDeleteModal from '../components/AgentDeleteModal';

const AgentsPage = () => {
  // État local pour les modals
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Récupération des agents via RTK Query
  const {
    data: agents = [],
    isLoading,
    error,
    refetch
  } = useGetAgentsQuery();

  // Handlers pour les modals
  const handleCreateAgent = () => {
    setOpenCreateModal(true);
  };

  const handleEditAgent = (agent) => {
    setSelectedAgent(agent);
    setOpenEditModal(true);
  };

  const handleDeleteAgent = (agent) => {
    setSelectedAgent(agent);
    setOpenDeleteModal(true);
  };

  const handleCloseModals = () => {
    setOpenCreateModal(false);
    setOpenEditModal(false);
    setOpenDeleteModal(false);
    setSelectedAgent(null);
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3 } }}>
      <Grid container spacing={3}>

        {/* Header Section */}
        <Grid item xs={12}>
          <Card
            elevation={3}
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              mb: 3
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                textAlign: { xs: 'center', sm: 'left' }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 0 } }}>
                  <Box sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    p: 1,
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <AgentIcon sx={{ fontSize: 40 }} />
                  </Box>
                  <Box>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                      Gestion des Agents IA
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      Créez, modifiez et surveillez vos agents intelligents
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateAgent}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                      },
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Créer un Agent
                  </Button>

                  <Tooltip title="Actualiser">
                    <Button
                      variant="outlined"
                      onClick={handleRefresh}
                      sx={{
                        color: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      <RefreshIcon />
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Error Alert */}
        {error && (
          <Grid item xs={12}>
            <Alert severity="error" sx={{ mb: 2 }}>
              Erreur lors du chargement des agents : {error.message || 'Erreur inconnue'}
            </Alert>
          </Grid>
        )}

        {/* Main Content */}
        <Grid item xs={12}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 3,
              minHeight: '400px'
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <AgentList
                agents={agents}
                isLoading={isLoading}
                onEditAgent={handleEditAgent}
                onDeleteAgent={handleDeleteAgent}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Stats Cards */}
        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Agents
              </Typography>
              <Typography variant="h3" color="primary">
                {agents.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Agents Actifs
              </Typography>
              <Typography variant="h3" color="success.main">
                {agents.filter(agent => agent.status === 'active').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                En Formation
              </Typography>
              <Typography variant="h3" color="warning.main">
                {agents.filter(agent => agent.status === 'training').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        onClick={handleCreateAgent}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1000
        }}
      >
        <AddIcon />
      </Fab>

      {/* Modals */}
      <AgentForm
        open={openCreateModal}
        onClose={handleCloseModals}
        mode="create"
        agent={null}
      />

      <AgentForm
        open={openEditModal}
        onClose={handleCloseModals}
        mode="edit"
        agent={selectedAgent}
      />

      <AgentDeleteModal
        open={openDeleteModal}
        onClose={handleCloseModals}
        agent={selectedAgent}
      />
    </Container>
  );
};

export default AgentsPage;
